import React, { useMemo, useState, useCallback } from "react";
import {
  Box, Button, Flex, Text, Tag, TagLabel, VStack, Select, useToast,
} from "@chakra-ui/react";
import { calculateBestGrafts } from "../utils/graftCalculator";

/** Mini list of added grafts for the current wound */
const AddedGraftsList = ({ graftsUsed = [], product, onRemoveGraft, formatter }) => (
  <Box mt={4}>
    <Text fontWeight="medium">Added Grafts:</Text>
    {graftsUsed.length === 0 && (
      <Text fontSize="sm" color="gray.500">No grafts added yet.</Text>
    )}
    {graftsUsed.map((g, idx) => {
      const sizeStr = String(g.size || g.availableSizes?.[0] || "")
        .toLowerCase()
        .replace(/\s/g, "");
      const [w, h] = sizeStr.split("x").map(Number);
      const quantity = parseInt(g.quantity);
      const units = (!isNaN(w) && !isNaN(h) && !isNaN(quantity)) ? w * h * quantity : 0;
      const unitPrice = parseFloat(g.productData?.price || product?.price || 0);
      const totalPrice = unitPrice * units;

      const imageUrl =
        typeof (g.productData?.imageUrl) === "string"
          ? `https://www.curagenesis.com/assets/img/graft/${g.productData.imageUrl}`
          : product?.imageUrl?.S
            ? `https://www.curagenesis.com/assets/img/graft/${product.imageUrl.S}`
            : undefined;

      return (
        <Flex key={`${idx}-${g.product}-${g.size}-${g.quantity}`} align="center" gap={3} mt={2} justify="space-between">
          {imageUrl && (
            <Box minW="60px" maxW="60px" mr={2}>
              <img
                src={imageUrl}
                alt={g.productData?.name || product?.name || "Graft"}
                style={{
                  width: "60px",
                  height: "60px",
                  objectFit: "contain",
                  borderRadius: "6px",
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                }}
              />
            </Box>
          )}
          <Box flex="1">
            <Text>
              {g.quantity}x {g.size} {g.productData?.name || product?.name}
              {g.originalSize && g.originalSize !== g.size && (
                <Text as="span" fontSize="xs" color="gray.500" ml={2}>
                  (Originally: <strong>{g.originalSize}</strong>)
                </Text>
              )}
            </Text>
            <Text fontSize="sm" color="gray.600">
              Unit Rate: {formatter.format(unitPrice)} |{" "}
              <strong>Total: {!isNaN(totalPrice) ? formatter.format(totalPrice) : "—"}</strong>
            </Text>
          </Box>
          {onRemoveGraft && (
            <Button size="xs" colorScheme="red" onClick={() => onRemoveGraft(g)} _hover={{ transform: "scale(1)" }}>
              Remove
            </Button>
          )}
        </Flex>
      );
    })}
  </Box>
);

/**
 * AdminGraftSelectorPro (NO AUTO SELECTION)
 *
 * Controlled selection:
 * - selectedProductId (string | "")
 * - setSelectedProductId(fn)
 *
 * Props:
 * - woundIndex, woundLength, woundWidth
 * - allProducts: tolerant of DynamoDB shapes ({ id|id.S, name|name.S, qCode, sizes[], price, imageUrl })
 * - allowMixBrands (bool)
 * - graftsUsed (array for this wound)
 * - onAddGraft(graft), onRemoveGraft(graft)
 */
const AdminGraftSelectorPro = ({
  woundIndex,
  woundLength,
  woundWidth,
  selectedProductId,
  setSelectedProductId,
  allProducts = [],
  allowMixBrands = false,
  graftsUsed = [],
  onAddGraft,
  onRemoveGraft,
}) => {
  const toast = useToast();

  // unwrap DynamoDB-ish values
  const unwrap = useCallback((val) => {
    if (val && typeof val === "object" && Object.keys(val).length === 1) {
      const k = Object.keys(val)[0];
      if (["S", "N", "BOOL"].includes(k)) return val[k];
    }
    return val;
  }, []);

  // normalize products
  const normalizedProducts = useMemo(() => {
    return (allProducts || []).map((p) => {
      const id = unwrap(p.id) ?? unwrap(p.productId);
      const name = unwrap(p.name) ?? unwrap(p.productName);
      const qCode = unwrap(p.qCode) ?? unwrap(p.QCode);
      const price = parseFloat(unwrap(p.price) ?? unwrap(p.Price) ?? 0);
      const sizes = Array.isArray(p.sizes?.L)
        ? p.sizes.L.map(unwrap).filter(Boolean)
        : Array.isArray(p.sizes)
          ? p.sizes
          : [];
      const imageUrl = unwrap(p.imageUrl) ?? unwrap(p.ImageUrl);
      return { ...p, id, name, qCode, price: isNaN(price) ? 0 : price, sizes, imageUrl };
    }).filter(p => !!p.id && !!p.name);
  }, [allProducts, unwrap]);

  const selectedProduct = useMemo(
    () => normalizedProducts.find(p => p.id === selectedProductId) || null,
    [normalizedProducts, selectedProductId]
  );

  const woundArea = (parseFloat(woundLength) || 0) * (parseFloat(woundWidth) || 0);
  const totalUnits = graftsUsed.reduce((sum, g) => {
    const [w, h] = String(g.size || "").toLowerCase().split("x").map(Number);
    const qty = parseInt(g.quantity);
    if (!isNaN(w) && !isNaN(h) && !isNaN(qty)) return sum + (w * h * qty);
    return sum;
  }, 0);
  const remaining = woundArea - totalUnits;

  const sortedSizes = useMemo(() => {
    return selectedProduct?.sizes?.slice().sort((a, b) => {
      const [aw, ah] = a.split("x").map(Number);
      const [bw, bh] = b.split("x").map(Number);
      return (aw * ah) - (bw * bh);
    }) || [];
  }, [selectedProduct]);

  const formatter = useMemo(() => new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2,
  }), []);

  const addGraft = (size) => {
    if (!selectedProduct) {
      toast({ title: "Pick a product first", status: "warning", duration: 1200 });
      return;
    }
    onAddGraft({
      qCode: selectedProduct.qCode,
      product: selectedProduct.id,
      productId: selectedProduct.id,
      productData: selectedProduct,
      size,
      originalSize: size,
      quantity: 1,
      availableSizes: selectedProduct.sizes || [],
      woundIndex,
    });
    toast({ title: `Added ${size}`, status: "success", duration: 1200 });
  };

  const handleAutoCalc = () => {
    if (!selectedProduct) {
      toast({ title: "Pick a product first", status: "warning", duration: 1200 });
      return;
    }
    const best = calculateBestGrafts(woundArea, selectedProduct.sizes);
    if (best.length) {
      // clear then add best combo
      if (typeof onRemoveGraft === "function") {
        graftsUsed.forEach(g => onRemoveGraft(g));
      }
      best.forEach(({ size, quantity }) => {
        onAddGraft({
          qCode: selectedProduct.qCode,
          product: selectedProduct.id,
          productId: selectedProduct.id,
          productData: selectedProduct,
          size,
          originalSize: size,
          quantity,
          availableSizes: selectedProduct.sizes || [],
          woundIndex,
        });
      });
      toast({ title: "Auto-calculated grafts added!", status: "success", duration: 1500 });
    }
  };

  const [showOther, setShowOther] = useState(false);
  const otherProducts = useMemo(() => {
    if (!selectedProduct || !allowMixBrands) return [];
    return normalizedProducts.filter(p => p.id !== selectedProduct.id);
  }, [normalizedProducts, selectedProduct, allowMixBrands]);

  return (
    <Box borderWidth="1px" borderRadius="lg" p={4} mt={4} bg="gray.50">
      <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
        <Text fontWeight="bold">Add Graft for Wound {woundIndex + 1}</Text>
        <Button
          size="sm"
          colorScheme="blue"
          variant="outline"
          isDisabled={!selectedProduct}
          onClick={handleAutoCalc}
          _hover={{ bg: "blue.50", color: "blue.600", borderColor: "inherit", transform: "scale(1)" }}
        >
          Auto-Calculate
        </Button>
      </Flex>

      {/* Always visible. Starts empty. No auto-select. */}
      <Box mb={3}>
        <Text fontSize="sm" color="gray.600" mb={1}>Brand / Product</Text>
        <Select
          size="sm"
          value={selectedProductId || ""}
          onChange={(e) => setSelectedProductId(e.target.value)}
          bg="white"
        >
          <option value="" disabled>Choose a product</option>
          {normalizedProducts
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(p => (
              <option key={p.id} value={p.id}>
                {p.name} {p.qCode ? `(QCode: ${p.qCode})` : ""}
              </option>
            ))}
        </Select>
      </Box>

      {selectedProduct ? (
        <>
          <Text mb={1}><strong>Product:</strong> {selectedProduct.name}</Text>
          <Text mb={2}><strong>Wound Size:</strong> {woundLength} x {woundWidth} cm = {(parseFloat(woundLength || 0) * parseFloat(woundWidth || 0)).toFixed(2)} cm²</Text>

          <Flex mt={2} align="center" gap={4} wrap="wrap">
            <Box>
              <Text><strong>Total Coverage:</strong> {totalUnits.toFixed(2)} cm²</Text>
            </Box>
            <Box>
              {remaining > 0 ? (
                <Tag colorScheme="green" size="lg"><TagLabel>{remaining.toFixed(2)} cm² remaining</TagLabel></Tag>
              ) : remaining === 0 ? (
                <Tag colorScheme="blue" size="lg"><TagLabel>Perfect Fit</TagLabel></Tag>
              ) : (
                <Tag
                  size="lg"
                  bg={Math.abs(remaining) < 2 ? "yellow.100" : Math.abs(remaining) < 3 ? "orange.200" : "red.200"}
                  color="black"
                >
                  <TagLabel>Over by {Math.abs(remaining).toFixed(2)} cm² (Wastage)</TagLabel>
                </Tag>
              )}
            </Box>
          </Flex>

          <Flex mt={1} bg="gray.200" h="12px" w="100%" borderRadius="md" overflow="hidden">
            <Box
              h="100%"
              bg={
                remaining >= 0
                  ? "green.400"
                  : Math.abs(remaining) < 2
                    ? "yellow.300"
                    : Math.abs(remaining) < 3
                      ? "orange.400"
                      : "red.500"
              }
              width={`${(parseFloat(woundLength) > 0 && parseFloat(woundWidth) > 0)
                ? Math.min((totalUnits / (parseFloat(woundLength) * parseFloat(woundWidth))) * 100, 100)
                : 0}%`}
              transition="width 0.3s ease"
            />
          </Flex>

          <VStack mt={3} align="stretch">
            <Text fontWeight="medium">Available Sizes:</Text>
            <Flex flexWrap="wrap" gap={2}>
              {sortedSizes.map(size => (
                <Tag
                  key={size}
                  size="lg"
                  variant="solid"
                  colorScheme="blue"
                  cursor="pointer"
                  onClick={() => addGraft(size)}
                >
                  <TagLabel>{size}</TagLabel>
                </Tag>
              ))}
            </Flex>
          </VStack>

          {/* Different grafts (other brands) — only when allowed */}
          {allowMixBrands && otherProducts.length > 0 && (
            <Box mt={4}>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => setShowOther(v => !v)}
                _hover={{ transform: "scale(1)" }}
              >
                {showOther ? "Hide" : "Different Grafts"}
              </Button>
              {showOther && otherProducts.map(op => {
                const sizes = (op.sizes || []).slice().sort((a, b) => {
                  const [aw, ah] = a.split("x").map(Number);
                  const [bw, bh] = b.split("x").map(Number);
                  return (aw * ah) - (bw * bh);
                });
                return (
                  <Box key={op.id} mt={2}>
                    <Text fontWeight="semibold">{op.name}</Text>
                    <Flex flexWrap="wrap" gap={2}>
                      {sizes.map(size => (
                        <Tag
                          key={`${op.id}-${size}`}
                          size="lg"
                          variant="solid"
                          colorScheme="gray"
                          cursor="pointer"
                          onClick={() => {
                            onAddGraft({
                              qCode: op.qCode,
                              product: op.id,
                              productId: op.id,
                              productData: op,
                              size,
                              originalSize: size,
                              quantity: 1,
                              availableSizes: op.sizes || [],
                              woundIndex,
                            });
                            toast({ title: `Added ${size} from ${op.name}`, status: "success", duration: 1200 });
                          }}
                        >
                          <TagLabel>{size}</TagLabel>
                        </Tag>
                      ))}
                    </Flex>
                  </Box>
                );
              })}
            </Box>
          )}

          <AddedGraftsList
            graftsUsed={graftsUsed}
            product={selectedProduct}
            onRemoveGraft={onRemoveGraft}
            formatter={formatter}
          />
        </>
      ) : (
        <Text color="gray.500">Select a product to see available sizes.</Text>
      )}
    </Box>
  );
};

export default AdminGraftSelectorPro;
